import { G0 } from "../math/constants";

/**
 * Operating conditions for engine calculations.
 */
export interface EngineConditions {
  /** Throttle level 0.0 - 1.0 */
  throttle: number;
  /** Ambient pressure in Pa (0 = vacuum) */
  ambientPressure: number;
  /** Available power in Watts (optional, for electric engines) */
  availablePower: number;
  /** Temperature in K (optional) */
  temperature: number;
}

/** Create vacuum engine conditions. */
export function vacuumConditions(throttle: number = 1.0): EngineConditions {
  return {
    throttle,
    ambientPressure: 0,
    availablePower: Number.MAX_VALUE,
    temperature: 300,
  };
}

/**
 * Interface for all propulsion models.
 */
export interface IPropulsionModel {
  readonly name: string;

  /** Thrust in Newtons at given conditions. */
  thrust(conditions: EngineConditions): number;

  /** Specific impulse in seconds at given conditions. */
  specificImpulse(conditions: EngineConditions): number;

  /** Exhaust velocity in m/s. = ISP * g0 */
  readonly exhaustVelocity: number;

  /** Mass flow rate in kg/s at given conditions. */
  massFlowRate(conditions: EngineConditions): number;

  /** Fuel required for a given delta-V (m/s) and ship mass (kg). From Tsiolkovsky. */
  fuelForDeltaV(deltaV: number, shipMass: number): number;

  /** Burn time in seconds for a given delta-V and ship mass. */
  burnTime(deltaV: number, shipMass: number): number;
}

/** Sea level pressure for interpolation (Pa) */
const SEA_LEVEL_PRESSURE = 101325.0;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Chemical rocket engine propulsion model.
 * Linearly interpolates between sea-level and vacuum performance based on ambient pressure.
 */
export class ChemicalEngine implements IPropulsionModel {
  readonly name: string;
  readonly thrustVacuum: number;      // N
  readonly thrustSeaLevel: number;    // N
  readonly ispVacuum: number;         // s
  readonly ispSeaLevel: number;       // s
  readonly minThrottle: number;       // 0-1
  readonly maxThrottle: number;       // 0-1

  constructor(
    name: string,
    thrustVacuum: number,
    thrustSeaLevel: number,
    ispVacuum: number,
    ispSeaLevel: number,
    minThrottle: number = 0.0,
    maxThrottle: number = 1.0,
  ) {
    this.name = name;
    this.thrustVacuum = thrustVacuum;
    this.thrustSeaLevel = thrustSeaLevel;
    this.ispVacuum = ispVacuum;
    this.ispSeaLevel = ispSeaLevel;
    this.minThrottle = minThrottle;
    this.maxThrottle = maxThrottle;
  }

  /** Exhaust velocity at vacuum (m/s). */
  get exhaustVelocity(): number {
    return this.ispVacuum * G0;
  }

  thrust(conditions: EngineConditions): number {
    const t = this.clampThrottle(conditions.throttle);
    const pressureFraction = Math.min(conditions.ambientPressure / SEA_LEVEL_PRESSURE, 1.0);
    const thrustValue = lerp(this.thrustVacuum, this.thrustSeaLevel, pressureFraction);
    return thrustValue * t;
  }

  specificImpulse(conditions: EngineConditions): number {
    const pressureFraction = Math.min(conditions.ambientPressure / SEA_LEVEL_PRESSURE, 1.0);
    return lerp(this.ispVacuum, this.ispSeaLevel, pressureFraction);
  }

  massFlowRate(conditions: EngineConditions): number {
    const ve = this.specificImpulse(conditions) * G0;
    if (ve <= 0) return 0;
    return this.thrust(conditions) / ve;
  }

  fuelForDeltaV(deltaV: number, shipMass: number): number {
    const ve = this.exhaustVelocity;
    if (ve <= 0) return Infinity;
    return shipMass * (1.0 - Math.exp(-deltaV / ve));
  }

  burnTime(deltaV: number, shipMass: number): number {
    const fuelNeeded = this.fuelForDeltaV(deltaV, shipMass);
    const conditions = vacuumConditions();
    const mdot = this.massFlowRate(conditions);
    if (mdot <= 0) return Infinity;
    return fuelNeeded / mdot;
  }

  private clampThrottle(throttle: number): number {
    if (throttle < this.minThrottle) return this.minThrottle;
    if (throttle > this.maxThrottle) return this.maxThrottle;
    return throttle;
  }
}
